/**
 * Per-answer state shared through `useModel('candidateContext')`: bookmarks,
 * reactions, cited sources and the generated synthesis.
 *
 * Follows the same queue-plus-Map caching pattern as `influencerContext`, but
 * with one queue per resource - see the comment on the refs below.
 *
 * Because this is a single shared model instance, every screen showing the same
 * answer sees the same bookmark state; a toggle on the thread page is already
 * reflected on the bookmarks page.
 */
import { useRef, useState } from 'react';
import {
  getBookmark,
  getMyBookmarks,
  addBookmark,
  removeBookmark,
  getFeedback,
  addFeedback,
  getReferenceEntitiesByCandidateId,
  getSynByCandidateId,
} from '@/services/rest/api';

export default () => {
  // One queue per resource rather than a single shared one: a slow synthesis
  // request should not hold up a bookmark toggle. Within a resource the calls
  // stay ordered, which is what keeps optimistic toggles consistent.
  const feedbackQueueRef = useRef(Promise.resolve());
  const bookmarkQueueRef = useRef(Promise.resolve());
  const referenceEntitiesQueueRef = useRef(Promise.resolve());
  const synQueueRef = useRef(Promise.resolve());

  // Values are wrapped in `{ obj }` so that "cached, and the answer is nothing"
  // stays distinguishable from "not cached yet" - a bare `undefined` value
  // could not express the difference.
  const feedbackMapRef = useRef(new Map<string, { obj: DATA.V2Feedback | undefined }>());
  const bookmarkMapRef = useRef(new Map<string, { obj: DATA.V2Bookmark | undefined }>());

  // Whether the bookmark map has been primed from the server. Consumers wait on
  // this before treating a cache miss as "not bookmarked".
  const [isInitedBookmarks, setIsInitedBookmarks] = useState<boolean>(false);

  /** Read this user's reaction to an answer, from cache when possible. */
  const doGetFeedback = (
    id: string,
    success: (obj?: DATA.V2Feedback) => void,
    failed: () => void,
  ) => {
    feedbackQueueRef.current = feedbackQueueRef.current.then(async () => {
      const result = feedbackMapRef.current.get(id);
      if (result) {
        success(result.obj);
      } else {
        try {
          const msg = await getFeedback(id, { skipErrorHandler: true });
          if (msg && msg.success) {
            feedbackMapRef.current.set(id, { obj: msg.data });
            success(msg.data);
          } else {
            failed();
          }
        } catch (error) {
          console.error('doGetFeedback:', error);
          failed();
        }
      }
    });
    return feedbackQueueRef.current;
  };

  /** Record a reaction and write it straight into the cache. */
  const doAddFeedback = (
    id: string,
    reaction: DATA.ReactionType,
    success: (obj: DATA.V2Feedback) => void,
    failed: () => void,
  ) => {
    feedbackQueueRef.current = feedbackQueueRef.current.then(async () => {
      try {
        const msg = await addFeedback(id, reaction, { skipErrorHandler: true });
        if (msg && msg.success && msg.data) {
          feedbackMapRef.current.set(id, { obj: msg.data });
          success(msg.data);
        } else {
          failed();
        }
      } catch (error) {
        console.error('doGetFeedback:', error);
        failed();
      }
    });
    return feedbackQueueRef.current;
  };

  /** Bookmark state for a single answer. Prefer `doGetMyBookmarks` in bulk. */
  const doGetBookmark = (
    id: string,
    success: (obj?: DATA.V2Bookmark) => void,
    failed: () => void,
  ) => {
    bookmarkQueueRef.current = bookmarkQueueRef.current.then(async () => {
      const result = bookmarkMapRef.current.get(id);
      if (result) {
        success(result.obj);
      } else {
        try {
          const msg = await getBookmark(id, { skipErrorHandler: true });
          if (msg && msg.success) {
            bookmarkMapRef.current.set(id, { obj: msg.data });
            success(msg.data);
          }
        } catch (error) {
          console.error('doGetBookmark:', error);
          failed();
        }
      }
    });
    return bookmarkQueueRef.current;
  };

  /**
   * Prime the bookmark cache with everything this user has bookmarked, so the
   * per-answer checks that follow are all cache hits.
   *
   * `isInitedBookmarks` flips in a `finally`, on purpose: even a failed load
   * must unblock consumers, otherwise the UI waits forever on a retry that the
   * user never triggers.
   */
  const doGetMyBookmarks = (success: () => void, failed: () => void) => {
    bookmarkQueueRef.current = bookmarkQueueRef.current.then(async () => {
      try {
        const msg = await getMyBookmarks({ skipErrorHandler: true });
        if (msg && msg.success && msg.data) {
          msg.data.forEach((v) => {
            bookmarkMapRef.current.set(v.candidateId, { obj: v });
          });
          success();
        } else {
          failed();
        }
      } catch (error) {
        console.error('doGetBookmark:', error);
        failed();
      } finally {
        setIsInitedBookmarks(true);
      }
    });
    return bookmarkQueueRef.current;
  };

  /**
   * Pick up to three of the most recent bookmarks not already on screen - the
   * "you saved these" rail.
   *
   * Sorting is done on the id itself: candidate ids are Mongo ObjectIds, whose
   * leading bytes are a timestamp, so ordering by id descending is ordering by
   * recency. `BigInt` is used because the ids are far too wide for a Number.
   */
  const doGetCandidateIdsByBookmark = (filterIds: Set<string>) => {
    const candidateIds: string[] = [];

    const sortedValues = Array.from(bookmarkMapRef.current.values())
      .filter((v) => v.obj?.candidateId)
      .sort((a, b) => (BigInt(b.obj!.candidateId) > BigInt(a.obj!.candidateId) ? 1 : -1));

    for (const value of sortedValues) {
      if (candidateIds.length >= 3) break;

      const { candidateId } = value.obj!;
      if (!filterIds.has(candidateId)) {
        candidateIds.push(candidateId);
      }
    }
    return candidateIds;
  };

  /** Bookmark an answer, updating the cache on success. */
  const doAddBookmark = (
    id: string,
    success: (obj: DATA.V2Bookmark) => void,
    failed: () => void,
  ) => {
    bookmarkQueueRef.current = bookmarkQueueRef.current
      .then(async () => {
        try {
          const msg = await addBookmark(id, { skipErrorHandler: true });
          if (msg && msg.success && msg.data) {
            bookmarkMapRef.current.set(id, { obj: msg.data });
            success(msg.data);
          }
        } catch (error) {
          console.error('doAddBookmark:', error);
          failed();
        }
      })
      .catch((err) => {
        // A rejection here would poison `bookmarkQueueRef` and silently kill
        // every later bookmark operation, so swallow it and keep the tail
        // resolved.
        console.error('doAddBookmark:', err);
        failed();
      });
    return bookmarkQueueRef.current;
  };

  /**
   * Remove a bookmark. The cache entry is set to `{ obj: undefined }` rather
   * than deleted, so the answer stays known-as-not-bookmarked instead of
   * reverting to a cache miss and triggering another fetch.
   */
  const doRemoveBookmark = (id: string, success: (obj: boolean) => void, failed: () => void) => {
    bookmarkQueueRef.current = bookmarkQueueRef.current.then(async () => {
      try {
        const msg = await removeBookmark(id, { skipErrorHandler: true });
        if (msg && msg.success && (msg.data === true || msg.data === false)) {
          if (msg.data === true) {
            bookmarkMapRef.current.set(id, { obj: undefined });
          }
          success(msg.data);
        }
      } catch (error) {
        console.error('doRemoveBookmark:', error);
        failed();
      }
    });
    return bookmarkQueueRef.current;
  };

  /** Sources cited by an answer. Not cached - the citation panel is opened once. */
  const doGetReferenceEntities = (
    id: string,
    success: (list: DATA.V2ReferenceEntity[]) => void,
    failed: () => void,
  ) => {
    referenceEntitiesQueueRef.current = referenceEntitiesQueueRef.current.then(async () => {
      try {
        const msg = await getReferenceEntitiesByCandidateId(id, { skipErrorHandler: true });
        if (msg && msg.success && msg.data) {
          success(msg.data);
        } else {
          failed();
        }
      } catch (error) {
        console.error('doRemoveBookmark:', error);
        failed();
      }
    });
    return referenceEntitiesQueueRef.current;
  };

  /**
   * Poll the synthesis (summary) generated for an answer.
   *
   * `isFinished` is false while the backend is still generating, so callers
   * re-invoke this until it flips.
   */
  const doSyn = (
    id: string,
    success: (obj: {
      isFinished: boolean;
      referenceEntities: DATA.V2ReferenceEntity[];
      mentionedEntities: DATA.V2ProfileFigure[];
      links: DATA.V2WebLink[];
    }) => void,
    failed: () => void,
  ) => {
    synQueueRef.current = synQueueRef.current.then(async () => {
      try {
        const msg = await getSynByCandidateId(id, { skipErrorHandler: true });
        if (msg && msg.success && msg.data) {
          success(msg.data);
        } else {
          failed();
        }
      } catch (error) {
        console.error('doSyn:', error);
        failed();
      }
    });
    return synQueueRef.current;
  };

  return {
    isInitedBookmarks,
    doGetFeedback,
    doAddFeedback,
    doGetBookmark,
    doGetMyBookmarks,
    doAddBookmark,
    doRemoveBookmark,
    doGetCandidateIdsByBookmark,
    doGetReferenceEntities,
    doSyn,
  };
};
